import React, {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import GamelightButton from "components/button";
import { SingleDatepicker } from "chakra-dayzed-datepicker";
import { LuImagePlus } from "react-icons/lu";
import { FaRegTrashAlt } from "react-icons/fa";
import { CalendarIcon } from "@chakra-ui/icons";
import {
  sendNotification,
  getNotificationById,
  uploadNotificationImage,
  getCampaigns,
} from "../services";
import { useLocation, useHistory } from "react-router-dom";
import usePartnerNotificationStore from "../store";
import { Select as ChakraSelect } from "chakra-react-select";
import moment from "moment";
import { Get } from "api";

const notificationSchema = z.object({
  platformId: z
    .string()
    .nonempty("Platform is required")
    .transform((val) => Number(val)),
  packageName: z
    .string()
    .nonempty("Package name is required")
    .regex(/^[a-zA-Z0-9._-]+$/, "Invalid package name format"),
  title: z
    .string()
    .nonempty("Title is required")
    .max(65, "Title cannot exceed 65 characters"),
  subtitle: z
    .string()
    .nonempty("Subtitle is required")
    .max(120, "Subtitle cannot exceed 120 characters"),
  scheduledDate: z.date({ required_error: "Please select a date" }),
});

type FormInputs = {
  platformId: string;
  packageName: string;
  title: string;
  subtitle: string;
  scheduledDate: Date;
};

type PackageOption = {
  label: string;
  value: string;
};

type PlatformOption = {
  label: string;
  value: string;
};

const NotificationForm: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const store = usePartnerNotificationStore();

  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const isUpdate = id ? true : false;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    control,
  } = useForm<FormInputs>({
    resolver: zodResolver(notificationSchema),
  });

  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [disabledDates, setDisabledDates] = useState<Set<number>>(new Set());
  const toast = useToast();

  const bannerInputRef = useRef<HTMLInputElement>(null);

  const packageName = watch("packageName");
  const platformId = watch("platformId");

  const loadPackages = useCallback(
    async (searchText = "", page?: number) => {
      if (isLoadingPackages) return;
      setIsLoadingPackages(true);

      try {
        const response = await getCampaigns({
          page:
            page ||
            (searchText ? store.searchCurrentPage : store.currentPackagePage),
          limit: 10,
          search: searchText,
        });

        const newOptions = response.map((pkg: any) => ({
          label: pkg.appName,
          value: pkg.packageName,
        }));

        if (searchText) {
          if (page === 1) {
            store.setSearchResults(newOptions);
          } else {
            store.appendSearchResults(newOptions);
          }
          store.setSearchHasMore(newOptions.length === 10);
        } else {
          if (page === 1) {
            store.setPackages(newOptions, 1);
          } else {
            store.appendPackages(newOptions);
          }
          store.setHasMore(newOptions.length === 10);
        }
      } catch (error) {
        console.log("Failed to load packages:", error);
      } finally {
        setIsLoadingPackages(false);
      }
    },
    [isLoadingPackages]
  );

  const handleSearch = useCallback(
    (text: string, action?: { action: string }) => {
      if (action?.action === "input-change") {
        if (text) {
          store.setSearchText(text);
          loadPackages(text, 1);
        } else {
          store.setSearchText("");
          store.setSearchResults([]);

          if (store.packageOptions.length === 0) {
            loadPackages("", 1);
          }
        }
      }
    },
    [loadPackages]
  );

  const handleMenuScrollToBottom = useCallback(() => {
    if (!isLoadingPackages) {
      if (store.searchText) {
        if (store.searchHasMore) {
          const nextPage = store.searchCurrentPage + 1;
          store.setSearchPage(nextPage);
          loadPackages(store.searchText, nextPage);
        }
      } else {
        if (store.hasMorePackages) {
          const nextPage = store.currentPackagePage + 1;
          store.setPage(nextPage);
          loadPackages("", nextPage);
        }
      }
    }
  }, [
    isLoadingPackages,
    store.hasMorePackages,
    store.searchHasMore,
    store.currentPackagePage,
    store.searchCurrentPage,
    loadPackages,
    store.searchText,
  ]);

  useEffect(() => {
    if (store.packageOptions.length === 0) {
      loadPackages("", 1);
    }
  }, []);

  const triggerBannerUpload = () => {
    bannerInputRef.current?.click();
  };

  const handleBannerUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "image/png") {
      setBannerImageFile(file);
      setBannerImage(URL.createObjectURL(file));
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchNotificationDetails = async (notificationId: string) => {
    try {
      setIsLoading(true);
      const response = await getNotificationById(notificationId);

      if (response.err) {
        history.replace("/notification-manager/create");
        return;
      }

      const scheduledDate = moment(response.scheduledAt).toDate();
      setValue("platformId", String(response.platformId));
      setValue("packageName", response.packageName);
      setValue("title", response.title);
      setValue("subtitle", response.subtitle);
      setValue("scheduledDate", scheduledDate);
      setBannerImage(response.imageUrl);
    } catch (error) {
      history.replace("/notification-manager/create");
      toast({
        title: "Error",
        description: "Failed to fetch notification details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchNotificationDetails(id);
    }
  }, [id]);

  const onSubmit = async (data: FormInputs) => {
    try {
      const formattedDate = moment(data.scheduledDate).format("YYYY-MM-DD");

      const payload = {
        platformId: Number(data.platformId),
        packageName: data.packageName,
        title: data.title,
        subtitle: data.subtitle,
        scheduledAt: formattedDate,
        ...(id && { id }),
      };

      const response = await sendNotification(payload);

      const notificationId = id || response?.id;

      if (!response.err) {
        toast({
          title: "Success",
          description: id
            ? "Notification updated successfully"
            : "Notification created successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        return toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      if (bannerImageFile) {
        try {
          const uploadResponse = await uploadNotificationImage(
            notificationId,
            bannerImageFile
          );
        } catch (imageError) {
          console.error("Image upload error:", imageError);
          return toast({
            title: "Warning",
            description: "Notification saved but image upload failed",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      }

      store.setShouldRefresh(true);
      history.push("/notification-manager");
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description: `Error: ${(error as Error).message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const watchAllFields = watch();

  const isFormValid = () => {
    return (
      watchAllFields.platformId &&
      watchAllFields.packageName &&
      watchAllFields.title &&
      watchAllFields.subtitle &&
      watchAllFields.scheduledDate &&
      Object.keys(errors).length === 0
    );
  };

  const fetchUnavailableDates = useCallback(async () => {
    if (!packageName || !platformId) {
      setDisabledDates(new Set());
      return;
    }

    try {
      const response = await Get(
        `/dashboard/partner/notification/unavailable-dates?platformId=${platformId}&packageName=${packageName}`
      );

      const timestamps = response.disabledDates.map((date: string) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      });
      setDisabledDates(new Set(timestamps));
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      toast({
        title: "Error",
        description: "Failed to fetch unavailable dates",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [packageName, platformId, toast]);

  useEffect(() => {
    fetchUnavailableDates();
  }, [fetchUnavailableDates]);

  useEffect(() => {
    setValue("scheduledDate", null);
  }, [packageName, platformId, setValue]);

  return (
    <Flex
      direction="column"
      width={{
        base: "100%",
        md: "70%",
      }}
      pt={{ base: "130px", md: "80px", xl: "80px" }}
    >
      <Box
        margin={"20px"}
        padding={10}
        gap={10}
        background={"#fff"}
        borderRadius="16px"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex justifyContent="space-between" gap={10}>
            <Flex flex={1} flexDirection={"column"}>
              <FormControl isInvalid={!!errors.packageName} mb="4">
                <FormLabel htmlFor="packageName">Game Name *</FormLabel>
                <Controller
                  control={control}
                  name="packageName"
                  render={({ field }) => (
                    <ChakraSelect<PackageOption, false>
                      {...field}
                      id="packageName"
                      placeholder="Select game name"
                      options={
                        store.searchText
                          ? store.searchResults
                          : store.packageOptions
                      }
                      isLoading={isLoadingPackages}
                      onInputChange={(text, action) =>
                        handleSearch(text, action)
                      }
                      onChange={(option) => {
                        field.onChange(option?.value || "");
                      }}
                      onBlur={() => {
                        handleSearch("", { action: "input-change" });
                      }}
                      value={
                        packageName
                          ? {
                              label:
                                (store.searchText
                                  ? store.searchResults
                                  : store.packageOptions
                                ).find((opt) => opt.value === packageName)
                                  ?.label || packageName,
                              value: packageName,
                            }
                          : null
                      }
                      isSearchable
                      isClearable
                      onMenuScrollToBottom={handleMenuScrollToBottom}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      chakraStyles={{
                        container: (provided) => ({
                          ...provided,
                          zIndex: 3,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          maxHeight: "400px",
                          overflowY: "auto",
                          zIndex: 9999,
                          position: "absolute",
                          width: "100%",
                          marginTop: "8px",
                          backgroundColor: "white",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          scrollBehavior: "smooth",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#888",
                            borderRadius: "4px",
                          },
                        }),
                      }}
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors.packageName?.message}
                </FormErrorMessage>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} mb="4">
                <FormControl isInvalid={!!errors.platformId}>
                  <FormLabel htmlFor="platformId">Platform *</FormLabel>
                  <Controller
                    control={control}
                    name="platformId"
                    render={({ field }) => (
                      <ChakraSelect<PlatformOption, false>
                        {...field}
                        id="platformId"
                        placeholder="Select platform"
                        options={[
                          { label: "Android", value: "1" },
                          { label: "iOS", value: "2" },
                        ]}
                        onChange={(option) => {
                          field.onChange(option?.value || "");
                        }}
                        value={
                          field.value
                            ? {
                                label: field.value === "1" ? "Android" : "iOS",
                                value: field.value,
                              }
                            : null
                        }
                        styles={{
                          menuList: (provided) => ({
                            ...provided,
                            boxShadow:
                              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                          }),
                        }}
                      />
                    )}
                  />
                  <FormErrorMessage>
                    {errors.platformId?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="scheduleDate">Scheduled Date *</FormLabel>
                  <InputGroup style={{ zIndex: 1 }}>
                    <SingleDatepicker
                      name="scheduledAt"
                      date={watch("scheduledDate")}
                      onDateChange={(date) => setValue("scheduledDate", date)}
                      minDate={new Date()}
                      disabled={!packageName || !platformId}
                      configs={{
                        dateFormat: "yyyy-MM-dd",
                      }}
                      disabledDates={disabledDates}
                    />
                    <InputRightElement pointerEvents="none">
                      <CalendarIcon color="gray.400" />
                    </InputRightElement>
                  </InputGroup>
                  {(!packageName || !platformId) && (
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {!packageName && !platformId
                        ? "Please select both package name and platform first"
                        : !packageName
                        ? "Please select a package name first"
                        : "Please select a platform first"}
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>

              <FormControl isInvalid={!!errors.title} mb="4">
                <FormLabel htmlFor="title">Title *</FormLabel>
                <Input
                  id="title"
                  placeholder="Notification title"
                  maxLength={65}
                  {...register("title")}
                />
                <Flex justifyContent="flex-end">
                  <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
                  <Text fontSize="xs" color="gray.500">
                    {watch("title")?.length || 0}/65
                  </Text>
                </Flex>
              </FormControl>

              <FormControl isInvalid={!!errors.subtitle} mb="4">
                <FormLabel htmlFor="subtitle">Subtitle *</FormLabel>
                <Input
                  id="subtitle"
                  placeholder="Notification subtitle"
                  maxLength={120}
                  {...register("subtitle")}
                />
                <Flex justifyContent="flex-end">
                  <FormErrorMessage>
                    {errors.subtitle?.message}
                  </FormErrorMessage>
                  <Text fontSize="xs" color="gray.500">
                    {watch("subtitle")?.length || 0}/120
                  </Text>
                </Flex>
              </FormControl>

              <GamelightButton
                marginTop={10}
                type="submit"
                isLoading={isSubmitting || isLoading}
                width="full"
                isDisabled={!isFormValid() || isSubmitting || isLoading}
              >
                {isUpdate ? "Update" : "Create"}
              </GamelightButton>
            </Flex>
            <HStack
              display={"flex"}
              alignItems="center"
              flexDirection={"column"}
              gap={2}
            >
              <input
                type="file"
                ref={bannerInputRef}
                onChange={handleBannerUpload}
                accept="image/png"
                style={{ display: "none" }}
              />
              <FormLabel>Notification Image</FormLabel>
              {bannerImage ? (
                <>
                  <Image
                    src={bannerImage ? bannerImage : null}
                    borderRadius="2xl"
                    width="100px"
                    height="100px"
                    objectFit="cover"
                  />

                  {isUpdate ? (
                    <Tooltip label="This button will allow you to add a new notification image.">
                      <Button
                        variant={"outline"}
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerBannerUpload();
                        }}
                        gap={2}
                      >
                        <Text fontSize="xs">Replace Image</Text>
                        <LuImagePlus style={{ marginBottom: 1 }} />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip label="This button will allow you to clear the notification image you've selected just before.">
                      <Button
                        variant={"outline"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBannerImage(null);
                          setBannerImageFile(null);
                          bannerInputRef.current!.value = "";
                        }}
                        gap={2}
                      >
                        <Text fontSize="xs">Delete Image</Text>
                        <FaRegTrashAlt style={{ marginBottom: 1 }} />
                      </Button>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Tooltip label="This button allows you to upload a notification image.">
                  <Button
                    variant={"outline"}
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerBannerUpload();
                    }}
                    gap={2}
                  >
                    <Text fontSize="xs">Add Image</Text>
                    <LuImagePlus style={{ marginBottom: 1 }} />
                  </Button>
                </Tooltip>
              )}
            </HStack>
          </Flex>
        </form>
      </Box>
    </Flex>
  );
};

export default NotificationForm;
